import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authService, LoginRequest } from '../services/authService';

interface LoginPageProps {
  onBack?: () => void;
  onNavigateToRegistration?: () => void;
  onNavigateToMain?: () => void;
  onNavigateToFirstTimeLogin?: (email: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ 
  onBack, 
  onNavigateToRegistration, 
  onNavigateToMain,
  onNavigateToFirstTimeLogin 
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [firstTimeEmail, setFirstTimeEmail] = useState('');
  const [showFirstTimeLogin, setShowFirstTimeLogin] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const request: LoginRequest = {
        email: email.trim(),
        password,
        rememberMe: false
      };
      
      const response = await authService.login(request);
      
      if (response.requiresTwoFactor) {
        // Handle 2FA - for now just show message
        setError('Kétfaktoros hitelesítés szükséges - implementáció folyamatban');
      } else {
        // Successful login - reload to main page
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hibás email vagy jelszó');
    } finally {
      setLoading(false);
    }
  };

  const handleFirstTimeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!firstTimeEmail.trim()) {
      setError('Kérjük adja meg az email címét');
      setLoading(false);
      return;
    }

    try {
      console.log('🔵 Sending reset code request for:', firstTimeEmail.trim());
      const response = await authService.sendResetCode(firstTimeEmail.trim());
      console.log('🟢 Response received:', response);
      
      // Development módban kiírjuk a kódot a konzolra
      if (response.developmentCode) {
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('🔐 JELSZÓ VISSZAÁLLÍTÁSI KÓD');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log(`📧 Email: ${firstTimeEmail.trim()}`);
        console.log(`🔑 Kód: ${response.developmentCode}`);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      } else {
        console.warn('⚠️ No developmentCode in response. Backend might not be in Development mode.');
      }
      
      console.log('🔵 Switching to email verification view...');
      setShowEmailVerification(true);
      setShowFirstTimeLogin(false);
      console.log('🟢 View switched successfully');
    } catch (error: any) {
      console.error('🔴 Error sending reset code:', error);
      setError(error.message || 'Hiba történt az email küldés során');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!verificationCode.trim()) {
      setError('Kérjük adja meg a hitelesítő kódot');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.verifyResetCode(firstTimeEmail, verificationCode.trim());
      if (response.isValid) {
        setShowEmailVerification(false);
        setShowPasswordReset(true);
      } else {
        setError('Érvénytelen hitelesítő kód');
      }
    } catch (error: any) {
      setError(error.message || 'Hiba történt a kód ellenőrzése során');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError('Kérjük töltse ki mind a két jelszó mezőt');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('A jelszavak nem egyeznek');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('A jelszónak legalább 6 karakter hosszúnak kell lennie');
      setLoading(false);
      return;
    }

    try {
      await authService.resetPassword(firstTimeEmail, verificationCode, newPassword);
      // Reset all states
      setShowPasswordReset(false);
      setShowEmailVerification(false);
      setShowFirstTimeLogin(false);
      setFirstTimeEmail('');
      setVerificationCode('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      alert('Jelszó sikeresen beállítva! Most már bejelentkezhet.');
    } catch (error: any) {
      setError(error.message || 'Hiba történt a jelszó beállítása során');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        backgroundColor: '#f0f3f6',
        padding: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Card sx={{ backgroundColor: 'white', maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            KipNet
          </Typography>
          
          {!showFirstTimeLogin && !showEmailVerification && !showPasswordReset ? (
            // Normal login form
            <Box component="form" onSubmit={handleLogin} sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Email cím"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                margin="normal"
                variant="outlined"
              />
              
              <TextField
                fullWidth
                label="Jelszó"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                margin="normal"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  backgroundColor: '#3a4651',
                  '&:hover': {
                    backgroundColor: '#2d3640',
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Bejelentkezés'}
              </Button>

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  vagy
                </Typography>
              </Divider>
              
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowFirstTimeLogin(true)}
                sx={{ 
                  mb: 2,
                  color: '#ff6b35',
                  borderColor: '#ff6b35',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 107, 53, 0.1)',
                    borderColor: '#e55a2b',
                  }
                }}
              >
                Elfelejtett jelszó
              </Button>

              {onNavigateToRegistration && (
                <Button
                  fullWidth
                  variant="text"
                  onClick={onNavigateToRegistration}
                  sx={{ color: '#3a4651' }}
                >
                  Nincs még fiókod? Regisztrálj itt
                </Button>
              )}
            </Box>
          ) : showFirstTimeLogin ? (
            // First time login form
            <Box component="form" onSubmit={handleFirstTimeSubmit} sx={{ mt: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom align="center">
                Első belépés
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Adja meg email címét a jelszó beállításához
              </Typography>
              
              <TextField
                fullWidth
                label="Email cím"
                type="email"
                value={firstTimeEmail}
                onChange={(e) => setFirstTimeEmail(e.target.value)}
                required
                margin="normal"
                variant="outlined"
                autoFocus
              />
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  backgroundColor: '#ff6b35',
                  '&:hover': {
                    backgroundColor: '#e55a2b',
                  }
                }}
              >
                Folytatás
              </Button>
              
              <Button
                fullWidth
                variant="text"
                onClick={() => setShowFirstTimeLogin(false)}
                sx={{ color: '#3a4651' }}
              >
                Vissza a bejelentkezéshez
              </Button>
            </Box>
          ) : showEmailVerification ? (
            // Email verification form
            <Box component="form" onSubmit={handleVerificationSubmit} sx={{ mt: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom align="center">
                Email hitelesítés
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Kérjük adja meg a {firstTimeEmail} címre küldött 6 jegyű hitelesítő kódot
              </Typography>
              
              <TextField
                fullWidth
                label="Hitelesítő kód"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                margin="normal"
                variant="outlined"
                inputProps={{ maxLength: 6 }}
              />
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ 
                  mt: 3, 
                  mb: 2, 
                  backgroundColor: '#e55a2b',
                  '&:hover': {
                    backgroundColor: '#d94d20',
                  },
                  '&:focus': {
                    borderColor: '#e55a2b',
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Hitelesítés'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setShowEmailVerification(false);
                  setShowFirstTimeLogin(true);
                  setVerificationCode('');
                  setError('');
                }}
                sx={{ color: '#3a4651' }}
              >
                Vissza
              </Button>
            </Box>
          ) : showPasswordReset ? (
            // Password reset form
            <Box component="form" onSubmit={handlePasswordReset} sx={{ mt: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom align="center">
                Új jelszó beállítása
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Adja meg az új jelszót a fiókjához
              </Typography>
              
              <TextField
                fullWidth
                label="Új jelszó"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                margin="normal"
                variant="outlined"
              />
              
              <TextField
                fullWidth
                label="Új jelszó megerősítése"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                margin="normal"
                variant="outlined"
              />
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ 
                  mt: 3, 
                  mb: 2, 
                  backgroundColor: '#e55a2b',
                  '&:hover': {
                    backgroundColor: '#d94d20',
                  },
                  '&:focus': {
                    borderColor: '#e55a2b',
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Jelszó beállítása'}
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setShowPasswordReset(false);
                  setShowEmailVerification(true);
                  setNewPassword('');
                  setConfirmPassword('');
                  setError('');
                }}
                sx={{ color: '#3a4651' }}
              >
                Vissza
              </Button>
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
